import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, CheckCircle, XCircle, AlertCircle, Info, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../common/Button';
import subscriptionService from '../../services/subscriptionService';

export interface CouponInputProps {
  onCouponValidated: (isValid: boolean, couponDetails: any) => void;
  onClearCoupon: () => void;
  className?: string;
  couponDetails?: any;
  initialCouponCode?: string;
}

// Improve deep comparison of coupon details objects
const isDeepEqual = (objA: any, objB: any): boolean => {
  if (objA === objB) return true;
  if (!objA || !objB) return false;
  
  // Compare primitive coupon properties that would affect UI
  const keysToCompare = ['code', 'isValid', 'discountType', 'value', 'freeMinutes', 'message'];
  
  return keysToCompare.every(key => objA[key] === objB[key]);
};

const arePropsEqual = (prevProps: CouponInputProps, nextProps: CouponInputProps) => {
  // Check if className has changed
  if (prevProps.className !== nextProps.className) {
    return false;
  }
  
  // Deep comparison for couponDetails
  if (prevProps.couponDetails === nextProps.couponDetails) {
    return true;
  }
  
  // If one is null/undefined and the other isn't, they're different
  if (!prevProps.couponDetails || !nextProps.couponDetails) {
    return prevProps.couponDetails === nextProps.couponDetails;
  }
  
  // Use deep comparison for the coupon details
  return isDeepEqual(prevProps.couponDetails, nextProps.couponDetails);
};

const CouponInput: React.FC<CouponInputProps> = React.memo(({
  onCouponValidated,
  className = '',
  couponDetails,
  onClearCoupon,
  initialCouponCode
}) => {
  const [couponCode, setCouponCode] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [validCoupon, setValidCoupon] = useState<any>(null);
  const previousCouponDetailsRef = useRef<any>(null);
  const skipEffectRef = useRef<boolean>(false);
  const onCouponValidatedRef = useRef(onCouponValidated);

  // Update the ref when the callback changes
  useEffect(() => {
    onCouponValidatedRef.current = onCouponValidated;
  }, [onCouponValidated]);
  
  // Set initial coupon details if provided
  useEffect(() => {
    // Skip if we're inside a validation cycle to avoid loops
    if (skipEffectRef.current) {
      skipEffectRef.current = false;
      return;
    }
    
    if (couponDetails) {
      // Skip updating if coupon details haven't meaningfully changed
      if (validCoupon && isDeepEqual(previousCouponDetailsRef.current, couponDetails)) {
        return;
      }
      
      console.log('CouponInput: Updating coupon details UI state');
      setValidCoupon(couponDetails);
      setValidationStatus(couponDetails.isValid ? 'success' : 'error');
      setValidationMessage(couponDetails.message || (couponDetails.isValid ? 'Coupon applied successfully' : 'Invalid coupon'));
      if (couponDetails.code) {
        setCouponCode(couponDetails.code);
      }
      
      // Update the ref for next comparison
      previousCouponDetailsRef.current = { ...couponDetails };
    }
  }, [couponDetails, validCoupon]);

  const validateCoupon = useCallback(async () => {
    if (!couponCode.trim()) {
      setValidationStatus('error');
      setValidationMessage('Please enter a coupon code');
      return;
    }

    try {
      setIsValidating(true);
      setValidationStatus('loading');
      setValidationMessage('Checking coupon...');

      // Set the skip flag before making the API call to prevent the useEffect loop
      skipEffectRef.current = true;
      
      const result = await subscriptionService.validateCoupon(couponCode.trim());
      console.log('CouponInput: Validation result:', result);
      
      if (result.isValid) {
        setValidationStatus('success');
        setValidationMessage(result.message || 'Coupon applied successfully');
        setValidCoupon(result);
        
        // Store the result as the previous value for comparison
        previousCouponDetailsRef.current = { ...result };
        
        // Call the parent callback using the ref to avoid stale closures
        onCouponValidatedRef.current(true, result);
      } else {
        setValidationStatus('error');
        setValidationMessage(result.message || 'Invalid coupon code');
        setValidCoupon(null);
        previousCouponDetailsRef.current = null;
        onCouponValidatedRef.current(false, result);
      }
    } catch (error: any) {
      setValidationStatus('error');
      setValidationMessage(error.message || 'Failed to validate coupon');
      setValidCoupon(null);
      previousCouponDetailsRef.current = null;
      onCouponValidatedRef.current(false, null);
    } finally {
      setIsValidating(false);
    }
  }, [couponCode]);

  // Always keep the latest validateCoupon in the ref
  const validateCouponRef = useRef<() => void>(() => {});
  useEffect(() => {
    validateCouponRef.current = validateCoupon;
  }, [validateCoupon]);

  // On mount, if initialCouponCode is provided, set and validate it
  useEffect(() => {
    if (
      initialCouponCode &&
      couponCode !== initialCouponCode // Only if not already set
    ) {
      console.log('Auto-filling and validating coupon:', initialCouponCode);
      setCouponCode(initialCouponCode);
      setTimeout(() => {
        validateCouponRef.current();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCouponCode]);

  const clearCoupon = useCallback(() => {
    setCouponCode('');
    setValidationStatus('idle');
    setValidationMessage('');
    setValidCoupon(null);
    onClearCoupon();
    previousCouponDetailsRef.current = null;
    
    // Set the skip flag to prevent effect loop
    skipEffectRef.current = true;
    
    // Notify parent that the coupon has been cleared using the ref
    onCouponValidatedRef.current(false, null);
  }, []);

  const handleCouponCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(e.target.value.toUpperCase());
  }, []);

  // Helper function to render validation status icon
  const renderStatusIcon = useCallback(() => {
    switch (validationStatus) {
      case 'loading':
        return <Loader2 className="w-4 h-4 text-primary-light dark:text-primary-dark animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />;
      default:
        return <Tag className="w-4 h-4 text-text-tertiary-light dark:text-text-tertiary-dark" />;
    }
  }, [validationStatus]);

  return (
    <div className={`w-full ${className}`}>
      <div className="flex-1">
        <div className="relative w-full">
          <div className={`text-start bg-background-light/70 dark:bg-background-dark/70 border border-border-light dark:border-border-dark rounded-lg ${
            validationStatus === 'error'
              ? 'border-red-500 dark:border-red-500'
              : validationStatus === 'success'
              ? 'border-green-600 dark:border-green-600'
              : 'border-border-light dark:border-border-dark'
          }`}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              disabled={isValidating || validationStatus === 'success'}
              className="text-base w-full rounded-lg bg-transparent border-none w-full focus:outline-none focus:ring-1 focus:ring-primary-light dark:focus:ring-primary-dark"
            />
          </div>
        </div>
        
        {validationMessage && (
          <div className={`mt-2 text-xs flex items-start ${
            validationStatus === 'error' ? 'text-red-500 dark:text-red-400' : 
            validationStatus === 'success' ? 'text-green-600 dark:text-green-400' :
            'text-text-secondary-light dark:text-text-secondary-dark'
          }`}>
            {validationStatus === 'error' && <AlertCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />}
            {validationStatus === 'success' && <CheckCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />}
            {validationStatus === 'loading' && <Loader2 className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0 animate-spin" />}
            <span>{validationMessage}</span>
          </div>
        )}
        
        <div className="mt-4">
          {validationStatus === 'success' ? (
            <Button
              size="md"
              variant="outline"
              onClick={clearCoupon}
              disabled={isValidating}
              className="whitespace-nowrap w-auto"
            >
              Remove Coupon
            </Button>
          ) : (
            <Button
              size="md"
              variant="primary"
              onClick={validateCoupon}
              disabled={isValidating || !couponCode.trim()}
              className="whitespace-nowrap w-auto"
              loading={isValidating}
              rightIcon={<Tag className="w-4 h-4" />}
            >
              Apply Coupon
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}, arePropsEqual);

export default CouponInput; 